import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { ENVIRONMENT_STATUS_SETTINGS_QUERY_KEY } from 'src/constants/query';
import EnvironmentService from 'src/services/environment.service';
import IconButton from 'src/components/IconButton';
import Iconify from 'src/components/Iconify';
import { pagesText } from 'src/constants/pagesText';
import { useUpdateStatusSettings } from 'src/hooks/customerEnv';

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#4B4B4B',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const StyledCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 2, 1),
  borderBottom: '1px solid #5B5B5B',
}));

const StyledCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  fontWeight: 600,
  color: '#fff',
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1.25, 2, 2),
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  lineHeight: 1.6,
  color: '#DDDDDD',
  marginBottom: theme.spacing(1.5),
}));

const InfoRow = styled(TableRow)({
  '& td': {
    borderBottom: '1px solid #5B5B5B',
    padding: '12px 16px',
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
});

const InfoLabel = styled(Typography)({
  fontSize: '14px',
  color: '#DDDDDD',
  fontWeight: 500,
});

const InfoLabelCell = styled(TableCell)({
  width: '75%',
});

const InfoValue = styled(Typography)({
  fontSize: '14px',
  color: '#fff',
  fontWeight: 400,
});

const EditableFieldCell = styled(TableCell)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const getReadinessDefaults = settings => ({
  minDeviceAgeDays: settings?.minDeviceAgeDays ?? 10,
  minCleanCheckInStreak: settings?.minCleanCheckInStreak ?? 22,
  maxLastCheckInAgeDays: settings?.maxLastCheckInAgeDays ?? 3,
  minIntervalBetweenCheckInHours: settings?.minIntervalBetweenCheckInHours ?? 24,
  healthyDeviceThresholdDays: settings?.healthyDeviceThresholdDays ?? 14,
  autoArchiveThresholdDays: settings?.autoArchiveThresholdDays ?? 60,
});

const statusSettingsSchema = Yup.object({
  minDeviceAgeDays: Yup.number()
    .typeError('Minimum Device Age must be a number')
    .integer('Minimum Device Age must be a whole number')
    .min(0, 'Minimum Device Age must be 0 or greater')
    .max(180, 'Minimum Device Age must be 180 or less'),
  minCleanCheckInStreak: Yup.number()
    .typeError('Minimum Clean Check-In Streak must be a number')
    .integer('Minimum Clean Check-In Streak must be a whole number')
    .min(0, 'Minimum Clean Check-In Streak must be 0 or greater')
    .max(180, 'Minimum Clean Check-In Streak must be 180 or less'),
  maxLastCheckInAgeDays: Yup.number()
    .typeError('Maximum Last Check-In Age must be a number')
    .integer('Maximum Last Check-In Age must be a whole number')
    .min(0, 'Maximum Last Check-In Age must be 0 or greater')
    .max(180, 'Maximum Last Check-In Age must be 180 or less'),
  minIntervalBetweenCheckInHours: Yup.number()
    .typeError('Minimum Interval Between Check-Ins must be a number')
    .integer('Minimum Interval Between Check-Ins must be a whole number')
    .min(0, 'Minimum Interval Between Check-Ins must be 0 or greater')
    .max(720, 'Minimum Interval Between Check-Ins must be 720 or less'),
  healthyDeviceThresholdDays: Yup.number()
    .typeError('Healthy Device Threshold must be a number')
    .integer('Healthy Device Threshold must be a whole number')
    .min(0, 'Healthy Device Threshold must be 0 or greater')
    .max(180, 'Healthy Device Threshold must be 180 or less'),
  autoArchiveThresholdDays: Yup.number()
    .typeError('Archive After Inactivity must be a number')
    .integer('Archive After Inactivity must be a whole number')
    .min(1, 'Archive After Inactivity must be 1 or greater')
    .max(3650, 'Archive After Inactivity must be 3650 or less'),
});

const EnvironmentStatusSettings = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [editingReadinessField, setEditingReadinessField] = useState(null);
  const [isSavingReadiness, setIsSavingReadiness] = useState(false);
  const [readinessValues, setReadinessValues] = useState(getReadinessDefaults());

  const updateReadinessSettingsMutation = useUpdateStatusSettings();

  const { data: readinessSettings, isLoading } = useQuery(
    [ENVIRONMENT_STATUS_SETTINGS_QUERY_KEY, customerEnvId],
    () => EnvironmentService.getStatusSettings({ customerEnvId }),
    { enabled: !!customerEnvId, refetchOnWindowFocus: false },
  );

  const handleStartReadinessEdit = useCallback(
    field => {
      setEditingReadinessField(field);
      setReadinessValues(getReadinessDefaults(readinessSettings));
    },
    [readinessSettings],
  );

  const handleCancelReadinessEdit = useCallback(() => {
    setEditingReadinessField(null);
    setIsSavingReadiness(false);
  }, []);

  const handleSaveReadinessField = useCallback(
    field => {
      if (isSavingReadiness) return;

      const currentSettings = getReadinessDefaults(readinessSettings);
      const nextValue = Number(readinessValues[field]);
      const currentValue = Number(currentSettings[field]);

      try {
        statusSettingsSchema.validateSyncAt(field, {
          ...currentSettings,
          [field]: nextValue,
        });
      } catch (validationError) {
        enqueueSnackbar(validationError.message, {
          variant: 'error',
          autoHideDuration: 3000,
        });
        return;
      }

      if (nextValue === currentValue) {
        handleCancelReadinessEdit();
        return;
      }

      setIsSavingReadiness(true);

      updateReadinessSettingsMutation.mutate(
        {
          customerEnvId,
          minDeviceAgeDays:
            field === 'minDeviceAgeDays' ? nextValue : Number(currentSettings.minDeviceAgeDays),
          minCleanCheckInStreak:
            field === 'minCleanCheckInStreak'
              ? nextValue
              : Number(currentSettings.minCleanCheckInStreak),
          maxLastCheckInAgeDays:
            field === 'maxLastCheckInAgeDays'
              ? nextValue
              : Number(currentSettings.maxLastCheckInAgeDays),
          minIntervalBetweenCheckInHours:
            field === 'minIntervalBetweenCheckInHours'
              ? nextValue
              : Number(currentSettings.minIntervalBetweenCheckInHours),
          healthyDeviceThresholdDays:
            field === 'healthyDeviceThresholdDays'
              ? nextValue
              : Number(currentSettings.healthyDeviceThresholdDays),
          autoArchiveThresholdDays:
            field === 'autoArchiveThresholdDays'
              ? nextValue
              : Number(currentSettings.autoArchiveThresholdDays),
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([ENVIRONMENT_STATUS_SETTINGS_QUERY_KEY, customerEnvId]);
            setEditingReadinessField(null);
            setIsSavingReadiness(false);
            enqueueSnackbar('Status settings updated successfully', {
              variant: 'success',
              autoHideDuration: 3000,
            });
          },
          onError: error => {
            setIsSavingReadiness(false);
            enqueueSnackbar(error?.message || pagesText.somethingWentWrong, {
              variant: 'error',
              autoHideDuration: 3000,
            });
          },
        },
      );
    },
    [
      customerEnvId,
      enqueueSnackbar,
      handleCancelReadinessEdit,
      isSavingReadiness,
      queryClient,
      readinessSettings,
      readinessValues,
      updateReadinessSettingsMutation,
    ],
  );

  const handleReadinessKeyPress = useCallback(
    (e, field) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveReadinessField(field);
      } else if (e.key === 'Escape') {
        handleCancelReadinessEdit();
      }
    },
    [handleCancelReadinessEdit, handleSaveReadinessField],
  );

  const renderReadinessField = (field, label) => {
    const currentSettings = getReadinessDefaults(readinessSettings);
    const isEditing = editingReadinessField === field;
    const value = isEditing ? readinessValues[field] : currentSettings[field];

    return (
      <EditableFieldCell>
        {isEditing ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <TextField
              value={readinessValues[field]}
              type="number"
              onChange={e =>
                setReadinessValues(prev => ({
                  ...prev,
                  [field]: e.target.value,
                }))
              }
              onKeyDown={e => handleReadinessKeyPress(e, field)}
              onBlur={() => {
                if (!isSavingReadiness) {
                  handleSaveReadinessField(field);
                }
              }}
              autoFocus
              size="small"
              disabled={isSavingReadiness}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#333',
                  color: '#fff',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: '#5B5B5B',
                  },
                  '&:hover fieldset': {
                    borderColor: '#7B7B7B',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7DFF92',
                  },
                },
              }}
            />
            <IconButton
              size="small"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSaveReadinessField(field)}
              title={`Save ${label}`}>
              <Iconify icon="ic:round-check" width={18} height={18} color="#7DFF92" />
            </IconButton>
            <IconButton
              size="small"
              onMouseDown={e => e.preventDefault()}
              onClick={handleCancelReadinessEdit}
              title={`Cancel ${label}`}>
              <Iconify icon="ic:round-close" width={18} height={18} color="#a2a2a2" />
            </IconButton>
          </Stack>
        ) : (
          <>
            <InfoValue>{value}</InfoValue>
            <IconButton
              size="small"
              onClick={() => handleStartReadinessEdit(field)}
              title={`Edit ${label}`}>
              <Iconify icon="ic:baseline-edit" width={18} height={18} color="#a2a2a2" />
            </IconButton>
          </>
        )}
      </EditableFieldCell>
    );
  };

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box>
      <StyledCard>
        <StyledCardHeader>
          <StyledCardTitle>WDAC Readiness Settings</StyledCardTitle>
        </StyledCardHeader>
        <StyledCardContent>
          <CardDescription>
            These settings help determine what qualifies a device as ready for WDAC enforcement.
            They control the age, clean check-in streak, recent activity, and check-in cadence we
            expect before a device is considered ready.
          </CardDescription>
          <Table>
            <TableBody>
              <InfoRow>
                <InfoLabelCell>
                  <InfoLabel>Minimum Device Age (days):</InfoLabel>
                </InfoLabelCell>
                {renderReadinessField('minDeviceAgeDays', 'Minimum Device Age')}
              </InfoRow>
              <InfoRow>
                <InfoLabelCell>
                  <InfoLabel>Minimum Clean Check-In Streak:</InfoLabel>
                </InfoLabelCell>
                {renderReadinessField('minCleanCheckInStreak', 'Minimum Clean Check-In Streak')}
              </InfoRow>
              <InfoRow>
                <InfoLabelCell>
                  <InfoLabel>Maximum Last Check-In Age (days):</InfoLabel>
                </InfoLabelCell>
                {renderReadinessField('maxLastCheckInAgeDays', 'Maximum Last Check-In Age')}
              </InfoRow>
              <InfoRow>
                <InfoLabelCell>
                  <InfoLabel>Minimum Interval Between Check-Ins (Hours):</InfoLabel>
                </InfoLabelCell>
                {renderReadinessField(
                  'minIntervalBetweenCheckInHours',
                  'Minimum Interval Between Check-Ins (Hours)',
                )}
              </InfoRow>
            </TableBody>
          </Table>
        </StyledCardContent>
      </StyledCard>

      <StyledCard>
        <StyledCardHeader>
          <StyledCardTitle>Healthy Device Threshold</StyledCardTitle>
        </StyledCardHeader>
        <StyledCardContent>
          <CardDescription>
            This threshold helps determine how recent a device&apos;s contact and WDAC heartbeat
            must be before it is treated as unhealthy. If we still have recent contact but do not
            have a recent heartbeat, the device is treated as a configuration issue.
          </CardDescription>
          <Table>
            <TableBody>
              <InfoRow>
                <InfoLabelCell>
                  <InfoLabel>Healthy Device Threshold (days):</InfoLabel>
                </InfoLabelCell>
                {renderReadinessField('healthyDeviceThresholdDays', 'Healthy Device Threshold')}
              </InfoRow>
            </TableBody>
          </Table>
        </StyledCardContent>
      </StyledCard>

      <StyledCard>
        <StyledCardHeader>
          <StyledCardTitle>Automatic Device Archiving</StyledCardTitle>
        </StyledCardHeader>
        <StyledCardContent>
          <CardDescription>
            Devices in this environment that have not communicated for this many days are archived
            automatically. An archived device is restored automatically the next time we receive
            data from it.
          </CardDescription>
          <Table>
            <TableBody>
              <InfoRow>
                <InfoLabelCell>
                  <InfoLabel>Archive After Inactivity (days):</InfoLabel>
                </InfoLabelCell>
                {renderReadinessField('autoArchiveThresholdDays', 'Archive After Inactivity')}
              </InfoRow>
            </TableBody>
          </Table>
        </StyledCardContent>
      </StyledCard>
    </Box>
  );
};

export default EnvironmentStatusSettings;
